import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { apiClient } from '../lib/apiClient';

const REGIONS = ['BW', 'BY', 'BE', 'BB', 'HB', 'HH', 'HE', 'MV', 'NI', 'NW', 'RP', 'SL', 'SN', 'ST', 'SH', 'TH'];
const CROPS = ['wheat', 'barley', 'rapeseed', 'corn', 'sugarbeet', 'potato', 'rye', 'oats', 'triticale', 'sunflower', 'soybeans', 'grassland'];
const LIVESTOCK = ['dairy', 'beef', 'pigs', 'poultry'];
const ENERGY = ['pv', 'biogas'];

export default function OnboardingWizard({ onComplete, onSkip }) {
  const { t } = useTranslation();
  const [step, setStep] = useState(0);
  const [data, setData] = useState({
    farmName: '',
    totalHa: '',
    region: '',
    crops: [],
    livestock: [],
    energy: [],
  });

  const toggleItem = (field, item) => {
    setData((d) => ({
      ...d,
      [field]: d[field].includes(item)
        ? d[field].filter((i) => i !== item)
        : [...d[field], item],
    }));
  };

  const handleFinish = async () => {
    // Save farm data to backend before completing
    try {
      const payload = {};
      if (data.farmName) payload.name = data.farmName;
      if (data.totalHa) payload.total_ha = parseFloat(data.totalHa);
      if (data.region) {
        // Onboarding region list is DE Bundesländer only (2-letter codes)
        // Farm DB needs "DE-BY" format; AT/CH/PL have separate onboarding (Phase 2)
        payload.region_code = `DE-${data.region}`;
        payload.country_code = 'DE';
      }
      if (data.crops.length > 0 || data.livestock.length > 0 || data.energy.length > 0) {
        payload.profile = {
          crops: data.crops,
          livestock_types: data.livestock,
          has_pv: data.energy.includes('pv'),
          has_biogas: data.energy.includes('biogas'),
        };
      }
      if (Object.keys(payload).length > 0) {
        await apiClient.patch('/api/v1/aif/auth/farm', payload);
      }
    } catch (_) {
      // Non-blocking — onboarding completes even if save fails
    }
    onComplete(data);
  };

  return (
    <div className="min-h-[100dvh] flex items-center justify-center px-4 bg-gradient-to-br from-aif-50 to-white dark:from-gray-950 dark:to-gray-900">
      <div className="w-full max-w-md">
        <div className="text-center mb-6">
          <h1 className="text-xl font-bold">{t('onboarding.title')}</h1>
          <p className="text-sm text-[var(--aif-text-muted)] dark:text-gray-400 mt-1">{t('onboarding.subtitle')}</p>
        </div>

        {/* Progress */}
        <div className="flex gap-2 mb-6">
          {[0, 1, 2].map((i) => (
            <div
              key={i}
              className={`flex-1 h-1.5 rounded-full transition-colors ${
                i <= step ? 'bg-aif-600' : 'bg-gray-200 dark:bg-gray-700'
              }`}
            />
          ))}
        </div>

        <div className="bg-[var(--aif-bg-surface)] rounded-2xl shadow-lg border border-[var(--aif-border-default)] p-6">
          {/* Step 1: Farm basics */}
          {step === 0 && (
            <div className="space-y-4">
              <h2 className="font-semibold text-lg">{t('onboarding.step1Title')}</h2>
              <div>
                <label className="block text-sm font-medium text-[var(--aif-text-secondary)] mb-1">
                  {t('onboarding.farmName')}
                </label>
                <input
                  type="text"
                  value={data.farmName}
                  onChange={(e) => setData((d) => ({ ...d, farmName: e.target.value }))}
                  placeholder={t('onboarding.farmNamePlaceholder')}
                  className="w-full px-4 py-2.5 rounded-xl border border-[var(--aif-border-default)] bg-[var(--aif-bg-input)] text-sm focus:outline-none focus:border-aif-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-[var(--aif-text-secondary)] mb-1">
                  {t('onboarding.totalHa')}
                </label>
                <input
                  type="number"
                  value={data.totalHa}
                  onChange={(e) => setData((d) => ({ ...d, totalHa: e.target.value }))}
                  placeholder={t('onboarding.totalHaPlaceholder')}
                  className="w-full px-4 py-2.5 rounded-xl border border-[var(--aif-border-default)] bg-[var(--aif-bg-input)] text-sm focus:outline-none focus:border-aif-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-[var(--aif-text-secondary)] mb-1">
                  {t('onboarding.region')}
                </label>
                <select
                  value={data.region}
                  onChange={(e) => setData((d) => ({ ...d, region: e.target.value }))}
                  className="w-full px-4 py-2.5 rounded-xl border border-[var(--aif-border-default)] bg-[var(--aif-bg-input)] text-sm focus:outline-none focus:border-aif-500"
                >
                  <option value="">--</option>
                  {REGIONS.map((r) => (
                    <option key={r} value={r}>{t(`regions.${r}`)}</option>
                  ))}
                </select>
              </div>
            </div>
          )}

          {/* Step 2: Crops */}
          {step === 1 && (
            <div className="space-y-4">
              <h2 className="font-semibold text-lg">{t('onboarding.step2Title')}</h2>
              <p className="text-sm text-[var(--aif-text-muted)] dark:text-gray-400">{t('onboarding.crops')}</p>
              <div className="grid grid-cols-2 gap-2">
                {CROPS.map((crop) => (
                  <button
                    key={crop}
                    onClick={() => toggleItem('crops', crop)}
                    className={`px-3 py-2.5 rounded-xl text-sm text-left border transition-colors ${
                      data.crops.includes(crop)
                        ? 'border-aif-500 bg-aif-50 dark:bg-aif-950 text-aif-700 dark:text-aif-400 font-medium'
                        : 'border-gray-200 dark:border-gray-700 hover:border-gray-300'
                    }`}
                  >
                    {t(`crops.${crop}`)}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Step 3: Livestock + Energy */}
          {step === 2 && (
            <div className="space-y-6">
              <div>
                <h2 className="font-semibold text-lg">{t('onboarding.step3Title')}</h2>
                <p className="text-xs text-[var(--aif-text-tertiary)] mt-0.5">{t('onboarding.optional')}</p>
              </div>
              <div>
                <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">{t('onboarding.livestock')}</h3>
                <div className="grid grid-cols-2 gap-2">
                  {LIVESTOCK.map((item) => (
                    <button
                      key={item}
                      onClick={() => toggleItem('livestock', item)}
                      className={`px-3 py-2.5 rounded-xl text-sm text-left border transition-colors ${
                        data.livestock.includes(item)
                          ? 'border-aif-500 bg-aif-50 dark:bg-aif-950 text-aif-700 dark:text-aif-400 font-medium'
                          : 'border-gray-200 dark:border-gray-700 hover:border-gray-300'
                      }`}
                    >
                      {t(`onboarding.${item}`)}
                    </button>
                  ))}
                </div>
              </div>
              <div>
                <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">{t('onboarding.energy')}</h3>
                <div className="grid grid-cols-2 gap-2">
                  {ENERGY.map((item) => (
                    <button
                      key={item}
                      onClick={() => toggleItem('energy', item)}
                      className={`px-3 py-2.5 rounded-xl text-sm text-left border transition-colors ${
                        data.energy.includes(item)
                          ? 'border-aif-500 bg-aif-50 dark:bg-aif-950 text-aif-700 dark:text-aif-400 font-medium'
                          : 'border-gray-200 dark:border-gray-700 hover:border-gray-300'
                      }`}
                    >
                      {t(`onboarding.${item}`)}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* Navigation */}
          <div className="flex items-center justify-between mt-6 pt-4 border-t border-gray-100 dark:border-gray-800">
            {step > 0 ? (
              <button
                onClick={() => setStep((s) => s - 1)}
                className="text-sm text-[var(--aif-text-muted)] hover:text-gray-700 dark:hover:text-gray-300"
              >
                {t('onboarding.back')}
              </button>
            ) : (
              <button
                onClick={onSkip}
                className="text-sm text-[var(--aif-text-tertiary)] hover:text-gray-600"
              >
                {t('onboarding.skip')}
              </button>
            )}

            {step < 2 ? (
              <button
                onClick={() => setStep((s) => s + 1)}
                className="px-6 py-2.5 bg-aif-700 hover:bg-aif-800 dark:bg-aif-600 dark:hover:bg-aif-700 text-white text-sm font-medium rounded-xl transition-colors"
              >
                {t('onboarding.next')}
              </button>
            ) : (
              <button
                onClick={handleFinish}
                className="px-6 py-2.5 bg-aif-700 hover:bg-aif-800 dark:bg-aif-600 dark:hover:bg-aif-700 text-white text-sm font-medium rounded-xl transition-colors"
              >
                {t('onboarding.finish')}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
